import { useTranslation } from 'react-i18next'
import {
    createAssetDraft,
    CreateAssetDraftPayload,
    submitAsset,
    updateAsset,
    createAssetVersion,
    CreateAssetVersionPayload,
} from '../../../services'
import { AssetDetail, DraftAsset } from '@/modules/Asset/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

interface UseAssetMutationsProps {
    getAssetIds: () => { assetId: string; versionId: string }
    setDraftAsset: (draft: DraftAsset | AssetDetail) => void
    onDraftSaved?: (draft: DraftAsset | AssetDetail) => void
    onAssetSubmitted?: () => void
}

export const useAssetMutations = ({
    getAssetIds,
    setDraftAsset,
    onDraftSaved,
    onAssetSubmitted,
}: UseAssetMutationsProps) => {
    const { t } = useTranslation('asset')

    const createDraftMutation = useMutationWithHandler(
        {
            mutationFn: createAssetDraft,
        },
        {
            successMessage: t('form.draftSavedSuccess'),
            errorMessage: t('form.draftSaveError'),
            onSuccess: (response) => {
                setDraftAsset(response.result)
                localStorage.setItem(
                    'draftAsset',
                    JSON.stringify(response.result)
                )
                onDraftSaved?.(response.result)
            },
            reactQueryKeys: [QUERY_KEYS.ASSETS_DRAFT, QUERY_KEYS.ASSET_DETAIL],
        }
    )

    const updateDraftMutation = useMutationWithHandler(
        {
            mutationFn: (data: Partial<CreateAssetDraftPayload>) => {
                const { assetId, versionId } = getAssetIds()
                return updateAsset(assetId, {
                    ...data,
                    asset_version_id: versionId,
                })
            },
        },
        {
            successMessage: t('form.draftUpdatedSuccess'),
            errorMessage: t('form.draftUpdateError'),
            onSuccess: (response) => {
                setDraftAsset(response.result)
                localStorage.setItem(
                    'draftAsset',
                    JSON.stringify(response.result)
                )
                onDraftSaved?.(response.result)
            },
            reactQueryKeys: [QUERY_KEYS.ASSETS_DRAFT, QUERY_KEYS.ASSET_DETAIL],
        }
    )

    const submitAssetMutation = useMutationWithHandler(
        {
            mutationFn: submitAsset,
        },
        {
            successMessage: t('form.submitProcessing'),
            errorMessage: t('form.submitError'),
            onSuccess: () => {
                onAssetSubmitted?.()
            },
            reactQueryKeys: [
                QUERY_KEYS.ASSETS,
                QUERY_KEYS.ASSETS_DRAFT,
                QUERY_KEYS.ASSET_DETAIL,
            ],
        }
    )

    const createVersionMutation = useMutationWithHandler(
        {
            mutationFn: (params: {
                assetId: string
                data: CreateAssetVersionPayload
            }) => createAssetVersion(params.assetId, params.data),
        },
        {
            successMessage: t('form.submitProcessing'),
            errorMessage: t('form.submitError'),
            onSuccess: () => {
                onAssetSubmitted?.()
            },
            reactQueryKeys: [QUERY_KEYS.ASSETS, QUERY_KEYS.ASSET_DETAIL],
        }
    )

    return {
        createDraftMutation,
        updateDraftMutation,
        submitAssetMutation,
        createVersionMutation,
        isSaving:
            createDraftMutation.isPending || updateDraftMutation.isPending,
        isSubmitting:
            submitAssetMutation.isPending || createVersionMutation.isPending,
    }
}
